"use client";

import { RefreshCw, Trash2, X } from "lucide-react";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { Modal } from "../../blocks/modal/modal";
import { Alert, Badge } from "../../elements";
import { Button } from "../../elements/button/button";
import { Image } from "../../elements/image/image";
import { Text } from "../../elements/text/text";
import { cn } from "../../lib/utils";
import {
  type AvailabilityLocation,
  type AvailabilityStatus,
  ProductInputQuantity,
} from "../../patterns/product-input-quantity/product-input-quantity";

// Quantity entry for a specific availability date/slot
export interface QuantityEntry {
  id: string;
  quantity: number;
  availabilityStatus: "available-now" | "future-availability" | "out-of-stock";
  availabilityDate?: string;
  onQuantityChange?: (quantity: number) => void;
}

export type UserType = "default" | "bob" | "pre-order";

type AlertStatus = {
  isVisible: boolean;
  maxAvailability: number;
};

// Variant-level notification types
export type VariantNotificationType = "warning" | "alert";

export interface VariantNotification {
  type: VariantNotificationType;
  message: string;
  isDismissible?: boolean;
}

export interface ProductVariant {
  id: string;
  imageUrl: string;
  imageAlt: string;
  color: string;
  frame?: string;
  lens?: string;
  productId: string;
  size: string;
  quantity: number;
  price: string;
  totalPrice: string;
  isNetPrice?: boolean;
  yourPrice?: string;
  dealerListPrice?: string;
  promoPrice?: string;
  promoDiscountPercent?: number;
  deliveryLocation: string;
  showAvailability?: boolean;
  merchandiseCategory?: string;
  // For pre-order variant: multiple quantity entries instead of single quantity
  availability?: {
    status: AvailabilityStatus;
    amount?: number;
    locations?: AvailabilityLocation[];
  };
  // For pre-order variant: multiple quantity entries per size
  quantityEntries?: QuantityEntry[];
  // Variant-level notification (warning or alert)
  notification?: VariantNotification;
  notificationKey?: string;
  isReviewOnly?: boolean;
  canDelete?: boolean;
  onQuantityChange?: (quantity: number) => void;
  onDelete?: () => Promise<void>;
  onDismissNotification?: () => void;
  onVariantClick?: () => void;
}

export interface CartProductBoxProps {
  country?: string;
  productName: string;
  productCode: string;
  totalQuantity?: number;
  totalPrice: string;
  variants: ProductVariant[];
  // When true, quantities are rendered as read-only text instead of inputs
  readOnly?: boolean;
  // Component variant: "default" or "pre-order"
  variant?: UserType;
  showProductCode?: boolean;
  showTotalQuantity?: boolean;
  showDeleteButton?: boolean;
  onDeleteAll?: () => Promise<void>;
  onUpdateQuantities?: (variants: ProductVariant[]) => void;
  /** When true, the footer button shows the `secondary` variant to indicate an in-progress update. */
  isUpdating?: boolean;
  /** When true, the Update button will be enabled. Parent should set this when any variant quantity differs from saved values. */
  hasQuantityChanges?: boolean;
  /** When provided, enables product detail page navigation on variant image/info click */
  onVariantClick?: (productCode: string, variantId: string) => void;
  className?: string;
  microcopies: {
    productCode: string;
    totalQuantity: string;
    yourPrice: string;
    dealerListPriceLabel?: string;
    promoPriceLabel?: string;
    quantity: string;
    increment: string;
    decrement: string;
    availableNow: string;
    itemsReadyForDelivery?: string;
    standardDealerPrice: string;
    tagNetPrice: string;
    futureAvailability: string;
    outOfStock: string;
    updateQuantities: string;
    deleteButton: string;
    orderLimitReachedTitle: string;
    orderLimitReachedDescription: string;
    deleteAllConfirmTitle?: string;
    deleteAllConfirmMessage?: string;
    deleteVariantConfirmTitle: string;
    deleteVariantConfirmMessage: string;
    confirmDelete?: string;
    cancel?: string;
    frameLabel?: string;
    lensLabel?: string;
    // Cart update dialog microcopies
    cartUpdateDialogTitle?: string;
    cartUpdateDialogDescription?: string;
    cartUpdateDialogButton?: string;
  };
  /** When true, shows the cart update notification dialog */
  showCartUpdateDialog?: boolean;
  /** Callback when the cart update dialog is closed */
  onCartUpdateDialogClose?: () => void;
  /** Callback when the "Review cart" button is clicked in the dialog */
  onReviewCart?: () => void;
}

const getTotalQuantity = (quantityEntries: QuantityEntry[]) =>
  quantityEntries.reduce((sum, entry) => sum + entry.quantity, 0);

// Helper to compute total quantity from variants (handles both default and pre-order)
const computeTotalQuantity = (variants: ProductVariant[]): number => {
  return variants.reduce((sum, v) => {
    if (v.quantityEntries && v.quantityEntries.length > 0) {
      // For pre-order variants: count the number of entries (rows), not the sum of quantities
      return sum + v.quantityEntries.length;
    }
    return sum + v.quantity;
  }, 0);
};

// Helper to handle quantity entry changes and propagate to variant
const handleEntryQuantityChange = (
  entry: QuantityEntry,
  variant: ProductVariant,
  newQuantity: number
) => {
  if (entry.onQuantityChange) {
    entry.onQuantityChange(newQuantity);
  }

  if (!variant.quantityEntries) {
    return;
  }

  if (!variant.onQuantityChange) {
    return;
  }

  const updatedQuantity = variant.quantityEntries.reduce((sum, e) => {
    const quantity = e.id === entry.id ? newQuantity : e.quantity;
    return sum + quantity;
  }, 0);

  variant.onQuantityChange(updatedQuantity);
};

// Helper to render quantity selectors based on variant type
const renderQuantitySelectors = (
  variant: ProductVariant,
  layout: "mobile" | "desktop",
  isPreOrder: boolean,
  readOnly: boolean | undefined,
  microcopies: CartProductBoxProps["microcopies"],
  maxQuantityEntries: number,
  alertHandler?: (status: {
    isVisible: boolean;
    maxAvailability: number;
  }) => void,
  country?: string,
  getDisplayQuantity?: (variantId: string, propQuantity: number) => number,
  updateLocalQuantity?: (variantId: string, newQuantity: number) => void
): ReactNode => {
  // Pre-order with multiple quantity entries
  if (
    isPreOrder &&
    variant.quantityEntries &&
    variant.quantityEntries.length > 0
  ) {
    return (
      <section
        className={cn(
          "gap-2",
          layout === "desktop"
            ? "flex w-lg items-end"
            : `grid grid-cols-${maxQuantityEntries} items-center`
        )}
      >
        {variant.quantityEntries.slice(0, maxQuantityEntries).map((entry) => (
          <ProductInputQuantity
            alertHandler={alertHandler}
            country={country}
            key={entry.id}
            microcopies={{
              quantity: microcopies.quantity,
              increment: microcopies.increment,
              decrement: microcopies.decrement,
              availableNow: microcopies.availableNow,
              itemsReadyForDelivery: microcopies.itemsReadyForDelivery,
              futureAvailability: microcopies.futureAvailability,
              outOfStock: microcopies.outOfStock,
            }}
            onQuantityChange={(newQuantity) =>
              handleEntryQuantityChange(entry, variant, newQuantity)
            }
            productId={entry.id}
            showAvailability={false}
            value={entry.quantity}
          />
        ))}
      </section>
    );
  }

  // Default single quantity selector
  if (readOnly) {
    return <Text variant="body-md-bold">{variant.quantity}</Text>;
  }

  // Use local quantity to prevent flickering during updates
  const displayQuantity =
    getDisplayQuantity?.(variant.id, variant.quantity) ?? variant.quantity;

  return (
    <ProductInputQuantity
      alertHandler={alertHandler}
      availability={variant.availability}
      availabilityIconsLayout="side"
      country={country}
      merchandiseCategory={variant.merchandiseCategory}
      microcopies={{
        quantity: microcopies.quantity,
        increment: microcopies.increment,
        decrement: microcopies.decrement,
        availableNow: microcopies.availableNow,
        itemsReadyForDelivery: microcopies.itemsReadyForDelivery,
        futureAvailability: microcopies.futureAvailability,
        outOfStock: microcopies.outOfStock,
      }}
      onQuantityChange={(q) => {
        updateLocalQuantity?.(variant.id, q);
        variant.onQuantityChange?.(q);
      }}
      productId={variant.productId}
      showAvailability={variant.showAvailability}
      value={displayQuantity}
    />
  );
};

// Helper to render product image
const renderProductImage = (variant: ProductVariant): ReactNode => (
  <Image
    alt={variant.imageAlt}
    aspectRatio="1:1"
    borderRadius="sm"
    className="bg-neutral-100"
    fill
    objectFit="cover"
    src={variant.imageUrl}
  />
);

// Helper to render product info (color, productId, optional price)
const renderProductInfo = (
  variant: ProductVariant,
  priceNode: ReactNode,
  isPreOrder: boolean,
  microcopies?: { frameLabel?: string; lensLabel?: string }
): ReactNode => (
  <>
    <div className="flex flex-col gap-0.5">
      {variant.frame && (
        <Text variant="body-sm-bold">
          {microcopies?.frameLabel}: {variant.frame}
        </Text>
      )}
      {variant.frame ? (
        <Text variant="body-sm-bold">
          {microcopies?.lensLabel}: {variant.lens || variant.color}
        </Text>
      ) : (
        <Text variant="body-sm-bold">{variant.lens || variant.color}</Text>
      )}
    </div>
    <Text color="muted" variant="body-sm">
      {variant.id}
    </Text>
    {isPreOrder && variant.promoPrice && priceNode}
    {isPreOrder && !variant.promoPrice && variant.yourPrice && (
      <Text variant="body-sm-bold">{variant.yourPrice}</Text>
    )}
  </>
);

// Helper to render quantity total
const renderQuantityTotal = (variant: ProductVariant): ReactNode => (
  <Text variant="body-md-bold">
    {variant.quantityEntries
      ? getTotalQuantity(variant.quantityEntries)
      : variant.quantity}
  </Text>
);

// Max number of quantity entries to show for pre-order variants
const MAX_QUANTITY_ENTRIES = 4;

// VariantAlert Component - Matches Figma design for warning/alert banners
interface VariantAlertProps {
  notification: VariantNotification;
  onDismiss?: () => void;
}

function VariantAlert(props: Readonly<VariantAlertProps>): ReactNode {
  const isWarning = props.notification.type === "warning";
  const isDismissible = props.notification.isDismissible ?? true;

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 px-4 py-1 pr-2",
        isWarning ? "bg-orange-500" : "bg-red-700"
      )}
      role="alert"
    >
      <Text color="white" variant="body-sm-bold">
        {props.notification.message}
      </Text>
      {isDismissible && (
        <Button
          aria-label="Dismiss notification"
          className="size-8 shrink-0 cursor-pointer rounded p-0 text-white hover:bg-black"
          onClick={props.onDismiss}
          size="icon"
          variant="ghost"
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  );
}

export function CartProductBox(
  props: Readonly<CartProductBoxProps>
): ReactNode {
  const [alertStatus, setAlertStatus] = useState<AlertStatus>({
    isVisible: false,
    maxAvailability: 0,
  });
  const isPreOrder = props.variant === "pre-order";
  // Calculate totalQuantity internally if not provided
  const totalQuantity =
    props.totalQuantity ?? computeTotalQuantity(props.variants);

  // State for delete confirmation modals
  const [deleteState, setDeleteState] = useState({
    showDeleteAllModal: false,
    isDeletingAll: false,
  });
  const hasEditableVariants = props.variants.some(
    (variant) => !variant.isReviewOnly
  );

  // Local quantity state to prevent flickering during updates
  const [localQuantities, setLocalQuantities] = useState<Map<string, number>>(
    new Map()
  );

  // Track previous state to detect when to sync
  const prevStateRef = useRef({
    isUpdating: props.isUpdating,
    variantIds: props.variants.map((v) => v.id).join(","),
  });

  // Sync local quantities with props when:
  // 1. Update completes (isUpdating: true -> false)
  // 2. Variant list changes (add/remove variants)
  const currentVariantIds = props.variants.map((v) => v.id).join(",");
  const shouldSync =
    (prevStateRef.current.isUpdating && !props.isUpdating) ||
    prevStateRef.current.variantIds !== currentVariantIds;

  if (shouldSync) {
    const syncedMap = new Map<string, number>();
    for (const variant of props.variants) {
      syncedMap.set(variant.id, variant.quantity);
    }
    setLocalQuantities(syncedMap);
  }

  // Update refs after sync check
  prevStateRef.current = {
    isUpdating: props.isUpdating,
    variantIds: currentVariantIds,
  };

  // Get display quantity: use local if exists, otherwise use prop
  const getDisplayQuantity = (
    variantId: string,
    propQuantity: number
  ): number => localQuantities.get(variantId) ?? propQuantity;

  // Update local quantity when user changes input
  const updateLocalQuantity = (variantId: string, newQuantity: number) => {
    setLocalQuantities((prev) => {
      const next = new Map(prev);
      next.set(variantId, newQuantity);
      return next;
    });
  };

  const updateDeleteState = (updates: Partial<typeof deleteState>) => {
    setDeleteState((prev) => ({ ...prev, ...updates }));
  };

  const handleDeleteAllClick = () => {
    updateDeleteState({ showDeleteAllModal: true });
  };

  const handleConfirmDeleteAll = async () => {
    updateDeleteState({ isDeletingAll: true });
    try {
      await props.onDeleteAll?.();
      updateDeleteState({ showDeleteAllModal: false });
    } finally {
      updateDeleteState({ isDeletingAll: false });
    }
  };

  const handleCancelDeleteAll = () => {
    updateDeleteState({ showDeleteAllModal: false });
  };

  const handleDeleteVariantClick = async (variantId: string) => {
    const variant = props.variants.find((v) => v.id === variantId);
    await variant?.onDelete?.();
  };

  return (
    <section
      className={cn(
        "flex flex-col rounded-lg border bg-white",
        "border-neutral-300",
        props.className
      )}
    >
      {/* Product box header */}
      <div
        className={cn(
          "flex flex-col gap-2 rounded-t-lg border-b p-4 md:flex-row md:items-start md:justify-between md:gap-4",
          "border-neutral-300"
        )}
      >
        <div className="flex flex-col gap-1">
          <Text variant="body-lg">{props.productName}</Text>
          {props.showProductCode && (
            <Text variant="body-md">
              {props.microcopies.productCode}: {props.productCode}
            </Text>
          )}
        </div>

        <div className="flex items-end justify-end gap-4">
          {props.showTotalQuantity && (
            <div className="flex flex-col items-end gap-0.5">
              <Text variant="body-lg">
                {props.microcopies.totalQuantity}: {totalQuantity}
              </Text>
              <Text variant="body-md">{props.totalPrice}</Text>
            </div>
          )}
          {props.showDeleteButton && (
            <Button
              aria-label={props.microcopies.deleteButton}
              disabled={props.variants.length === 0}
              onClick={handleDeleteAllClick}
              size="icon"
              variant="ghost"
            >
              <Trash2 className="size-5" />
            </Button>
          )}
        </div>
      </div>

      {alertStatus.isVisible && (
        <div className="m-4">
          <Alert
            microcopies={{
              title: props.microcopies.orderLimitReachedTitle,
              description:
                props.microcopies.orderLimitReachedDescription.replace(
                  "{EffectiveLimit}",
                  alertStatus.maxAvailability.toString()
                ),
            }}
            variant="warning"
          />
        </div>
      )}

      {/* Product variants list */}
      <div className="flex flex-col p-4">
        {props.variants.map((variant, index) => {
          let priceNode: ReactNode = null;
          const isVariantReadOnly = props.readOnly || variant.isReviewOnly;
          const canDeleteVariant =
            props.showDeleteButton && (variant.canDelete ?? true);

          if (variant.promoPrice) {
            priceNode = (
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-baseline gap-1">
                  <Text variant="body-sm">
                    {props.microcopies.standardDealerPrice}
                  </Text>
                  <Text style="line-through" variant="body-sm-bold">
                    {variant.dealerListPrice}
                  </Text>
                  {variant.promoDiscountPercent && (
                    <Badge
                      fontWeight="bold"
                      text={`-${variant.promoDiscountPercent}%`}
                      variant="outline"
                    />
                  )}
                </div>
                <div className="flex flex-wrap items-baseline gap-1">
                  <Text variant="body-sm">
                    {props.microcopies.promoPriceLabel}
                  </Text>
                  <Text color="red" variant="body-sm-bold">
                    {variant.promoPrice}
                  </Text>
                </div>
              </div>
            );
          } else if (variant.yourPrice) {
            priceNode =
              props.variant === "bob" ? (
                <div className="flex flex-1 flex-col gap-1">
                  <div className="flex flex-wrap items-baseline gap-1">
                    <Text variant="body-sm">
                      {props.microcopies.standardDealerPrice}
                    </Text>
                    <Text variant="body-sm">{variant.dealerListPrice}</Text>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-1">
                    <Text variant="body-sm">
                      {props.microcopies.yourPrice}:
                    </Text>
                    <Text variant="body-sm-bold">{variant.yourPrice}</Text>
                    {variant.isNetPrice && (
                      <Badge
                        text={props.microcopies.tagNetPrice}
                        variant="default"
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-1 flex-wrap items-baseline gap-1">
                  <Text variant="body-sm">{props.microcopies.yourPrice}:</Text>
                  <Text variant="body-sm-bold">{variant.yourPrice}</Text>
                </div>
              );
          }

          return (
            <section
              className={cn(
                "mb-2 flex flex-col",
                index !== props.variants.length - 1 && "rounded border-b",
                // Apply border color based on notification type
                variant.notification?.type === "warning" &&
                  "border border-orange-500",
                variant.notification?.type === "alert" &&
                  "border border-red-600",
                !variant.notification && "border-neutral-200"
              )}
              key={variant.id}
            >
              {/* Variant Notification Banner */}
              {variant.notification && (
                <VariantAlert
                  notification={variant.notification}
                  onDismiss={() => {
                    // For out-of-stock alerts (red background):
                    // - Review-only items (synthetic): dismiss notification to remove from view
                    // - Real cart items: delete via API
                    if (variant.notification?.type === "alert") {
                      if (variant.isReviewOnly) {
                        variant.onDismissNotification?.();
                      } else {
                        variant.onDelete?.();
                      }
                    } else {
                      // For warnings (orange background), just dismiss the notification
                      variant.onDismissNotification?.();
                    }
                  }}
                />
              )}

              {/* Variant Content - greyed out for alert (out of stock) notifications */}
              <div
                className={cn(
                  "flex flex-col gap-3 p-4 md:grid md:items-center md:gap-6",
                  isPreOrder
                    ? "md:grid-cols-[1fr_2fr_auto_auto]"
                    : "md:grid-cols-[2fr_2fr_1fr_2fr_1.5fr]",
                  variant.notification?.type === "alert" && "opacity-50"
                )}
              >
                {/* Mobile Layout: Image on left, Product info column on right */}
                <div className="flex gap-4 py-2 md:hidden">
                  {/* Product Image - 64x64 */}
                  {props.onVariantClick ? (
                    <Button
                      className={cn("size-16 shrink-0 p-0")}
                      onClick={() =>
                        props.onVariantClick?.(props.productCode, variant.id)
                      }
                      unstyled={true}
                    >
                      {renderProductImage(variant)}
                    </Button>
                  ) : (
                    <div className="size-16 shrink-0">
                      {renderProductImage(variant)}
                    </div>
                  )}

                  {/* Product Info Column */}
                  <div className="flex flex-1 flex-col gap-2">
                    {/* Color + Product ID row */}
                    <div className="flex items-start gap-1.5">
                      {props.onVariantClick ? (
                        <Button
                          className={cn(
                            "flex flex-1 flex-col items-start gap-1 p-0 text-left"
                          )}
                          onClick={() =>
                            props.onVariantClick?.(
                              props.productCode,
                              variant.id
                            )
                          }
                          variant="ghost"
                        >
                          {renderProductInfo(
                            variant,
                            priceNode,
                            true,
                            props.microcopies
                          )}
                        </Button>
                      ) : (
                        <div className="flex flex-1 flex-col gap-1">
                          {renderProductInfo(
                            variant,
                            priceNode,
                            true,
                            props.microcopies
                          )}
                        </div>
                      )}
                    </div>

                    {/* Size + Quantity Selector row */}
                    <div className="flex items-center justify-between gap-8">
                      <Text variant="body-md-bold">{variant.size}</Text>
                      <div className="flex items-center gap-2.5">
                        {renderQuantitySelectors(
                          variant,
                          "mobile",
                          isPreOrder,
                          isVariantReadOnly,
                          props.microcopies,
                          MAX_QUANTITY_ENTRIES,
                          (status: AlertStatus) => setAlertStatus(status),
                          props.country,
                          getDisplayQuantity,
                          updateLocalQuantity
                        )}
                      </div>
                    </div>

                    {/* Pre-order Quantity total - Mobile */}
                    {isPreOrder && (
                      <div className="flex justify-end">
                        {renderQuantityTotal(variant)}
                      </div>
                    )}

                    {/* Price + Delete button row */}
                    <div className="flex items-center justify-end gap-4">
                      <Text variant="body-md">{variant.totalPrice}</Text>
                      {canDeleteVariant && (
                        <Button
                          aria-label={props.microcopies.deleteButton}
                          onClick={() => handleDeleteVariantClick(variant.id)}
                          size="icon"
                          variant="ghost"
                        >
                          <Trash2 className="size-5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desktop Layout: Column 1 - Image + Info Section */}

                {props.onVariantClick ? (
                  <div className="items-left hidden gap-8 md:flex">
                    <Button
                      className={cn(
                        "flex cursor-pointer justify-start gap-8 text-left no-underline hover:no-underline"
                      )}
                      onClick={(
                        e: React.MouseEvent<
                          HTMLAnchorElement | HTMLButtonElement
                        >
                      ) => {
                        e.preventDefault();
                        props.onVariantClick?.(props.productCode, variant.id);
                      }}
                      unstyled={true}
                      variant="link"
                    >
                      <div className="size-10 shrink-0">
                        {renderProductImage(variant)}
                      </div>
                      <div className="flex flex-col justify-center gap-1">
                        {renderProductInfo(
                          variant,
                          priceNode,
                          isPreOrder,
                          props.microcopies
                        )}
                      </div>
                    </Button>
                  </div>
                ) : (
                  <div className="hidden gap-3 md:flex">
                    <div className="size-20 shrink-0">
                      {renderProductImage(variant)}
                    </div>
                    <div className="flex flex-col justify-center gap-1">
                      {renderProductInfo(
                        variant,
                        priceNode,
                        isPreOrder,
                        props.microcopies
                      )}
                    </div>
                  </div>
                )}

                {/* Column 2: Price Information - Desktop (hidden for pre-order) */}
                {!isPreOrder && (
                  <div className="hidden items-center justify-center md:flex">
                    {priceNode}
                  </div>
                )}

                {/* Column 3: Delivered From - Desktop (hidden for pre-order) */}
                {!isPreOrder && (
                  <div className="hidden flex-col items-center justify-center gap-0.5 md:flex">
                    {/* Delivery location placeholder */}
                  </div>
                )}

                {/* Column 4 (or Column 2 for pre-order): Size + Quantity - Desktop */}
                <div className="hidden items-center gap-8 md:flex">
                  <span className="w-8 shrink-0">
                    <Text variant="body-md-bold">{variant.size}</Text>
                  </span>
                  {renderQuantitySelectors(
                    variant,
                    "desktop",
                    isPreOrder,
                    isVariantReadOnly,
                    props.microcopies,
                    MAX_QUANTITY_ENTRIES,
                    (status: AlertStatus) => setAlertStatus(status),
                    props.country,
                    getDisplayQuantity,
                    updateLocalQuantity
                  )}
                </div>

                {/*Column: quantity total for pre-order-Desktop*/}
                {isPreOrder && (
                  <div className="hidden items-center justify-center md:flex">
                    {renderQuantityTotal(variant)}
                  </div>
                )}

                {/* Column 6 (or Column 4/5 for pre-order): Total Price + Delete - Desktop */}
                <div className="hidden items-center justify-end gap-1 md:flex">
                  <Text variant="body-md">{variant.totalPrice}</Text>
                  {canDeleteVariant && (
                    <Button
                      aria-label={props.microcopies.deleteButton}
                      onClick={() => handleDeleteVariantClick(variant.id)}
                      size="icon"
                      variant="ghost"
                    >
                      <Trash2 className="size-5" />
                    </Button>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* Footer with update button (hidden in readonly mode) */}
      {!props.readOnly && hasEditableVariants && (
        <div
          className={cn("flex justify-end border-t p-4", "border-neutral-200")}
        >
          <Button
            aria-busy={props.isUpdating}
            disabled={props.isUpdating || !props.hasQuantityChanges}
            onClick={() => {
              // For pre-order variants, compute total quantity from entries
              // For default variants, use the existing quantity value
              const updatedVariants = props.variants
                .filter((variant) => !variant.isReviewOnly)
                .map((v) => {
                  const computedQuantity =
                    isPreOrder &&
                    v.quantityEntries &&
                    v.quantityEntries.length > 0
                      ? getTotalQuantity(v.quantityEntries)
                      : v.quantity;

                  return {
                    ...v,
                    quantity: computedQuantity,
                  };
                });
              props.onUpdateQuantities?.(updatedVariants);
            }}
            variant={props.isUpdating ? "secondary" : "default"}
          >
            <RefreshCw
              className={cn("size-4", props.isUpdating ? "animate-spin" : "")}
            />
            {props.microcopies.updateQuantities}
          </Button>
        </div>
      )}

      {/* Delete All Confirmation Modal */}
      <Modal
        isLoading={deleteState.isDeletingAll}
        isOpen={deleteState.showDeleteAllModal}
        isPrimaryButtonDisabled={deleteState.isDeletingAll}
        onClose={() => updateDeleteState({ showDeleteAllModal: false })}
        onPrimaryAction={() => {
          void handleConfirmDeleteAll();
        }}
        onSecondaryAction={handleCancelDeleteAll}
        primaryButtonText={props.microcopies.confirmDelete}
        secondaryButtonText={props.microcopies.cancel}
        title={props.microcopies.deleteVariantConfirmTitle || ""}
      >
        <Text variant="body-md">
          {props.microcopies.deleteVariantConfirmMessage || ""}
        </Text>
      </Modal>
    </section>
  );
}
